import React, { useEffect, useState, useRef } from 'react';
import { graphql } from 'gatsby';
//@ts-ignore
import * as styles from './ProductComparison.module.css';
import {
    KontentStringValue,
    CTAConfiguration,
    BrandColor,
    KontentRichText,
} from '../../../types/KontentProps';
import {
    getMultiChoice,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    imagePreset,
    getDisplayDimensions,
} from '../Global/DataUtils';
import LazyImage from '../Global/LazyImage';
import {
    MaterialData,
    ProductComparisonData,
} from '../../templates/StructuredDataContext';
import RichText from '../Global/RichText';
import CtaLink from '../Global/CtaLink';
//@ts-ignore
import { BuildLinkHref } from '../Global/BuildLinkHref';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
//@ts-ignore
import ArrowLgButton from '../Global/ArrowLgButton';
import PlainTextElement from '../Global/Headlines';

type columnProducts = number;
type colWidth = number;
type width = number;

type product = {
    elements: CTAConfiguration & {
        brand: {
            value: {
                codename: string;
            }[];
        };
        comparative_aspect: {
            value: {
                codename: string;
            }[];
        };
        display_name: KontentRichText;
        image: {
            value: {
                elements: {
                    file: {
                        value: {
                            url: string;
                        }[];
                    };
                };
            }[];
        };
        master_product: {
            value: {};
        };
        material: {
            value: [];
        };
    };
};

export interface ProductComparisonProps extends CTAConfiguration {
    headline: KontentStringValue;
    comparative_aspect: {
        value: {
            codename: string;
            name: string;
        }[];
    };
    category_a_label: KontentStringValue;
    category_a_master_color: BrandColor;
    category_a_products: {
        value: ProductComparisonData[];
    };
    category_b_label: KontentStringValue;
    category_b_master_color: BrandColor;
    category_b_products: {
        value: ProductComparisonData[];
    };
    compare_cta_label: KontentStringValue;
    cta_configuration__cta_location: any;
    materials: {
        value: MaterialData[];
    };
    kontentItemId?: string;
}

const ProductComparison: React.FC<ProductComparisonProps> = (
    props,
    {
        headline,
        cta_configuration__cta_label,
        cta_configuration__cta_window_action,
    }
) => {
    const categoryAProducts = props.category_a_products;
    const categoryBProducts = props.category_b_products;
    const categoryALabel = props.category_a_label.value;
    const categoryBLabel = props.category_b_label.value;
    const compareCtaLabel = props.compare_cta_label.value;
    const materials = props.materials.value;
    const breakpoints = useBreakpoint();
    const colWidth = 200;
    const [width, setWidth] = useState(0);
    const [leftA, setLeftA] = useState(0);
    const [leftB, setLeftB] = useState(0);
    const columnWrap = useRef(null);
    const colA = useRef(null);
    const columnProducts =
        categoryAProducts.value.length + categoryBProducts.value.length + 1;
    const [currentAProduct, setCurrentAProduct] = useState(0);
    const [currentAPosition, setCurrentAPosition] = useState(0);
    const dotsToShowCatA = categoryAProducts.value.length;
    const [currentBProduct, setCurrentBProduct] = useState(0);
    const [currentBPosition, setCurrentBPosition] = useState(0);
    const dotsToShowCatB = categoryBProducts.value.length;
    const [compareProductA, setCompareProductA] = useState<product>();
    const [compareProductB, setCompareProductB] = useState<product>();
    const [comparisonValueA, setComparisonValueA] = useState(-1);
    const [comparisonValueB, setComparisonValueB] = useState(-1);
    const [showComparison, setShowComparison] = useState(false);
    const ctaWindowAction = getMultiChoice(
        props.cta_configuration__cta_window_action
    );

    let newWidth = setNewWidth(columnProducts, width, colWidth);

    const downloadCTA = () => {
        switch (props.cta_configuration__cta_location.value[0]?.__typename) {
            case 'kontent_item_layout___master_page':
                return props.cta_configuration__cta_location.value[0]?.elements
                    .slug?.url;
            case 'kontent_item_content_item___third_party_link':
                return props.cta_configuration__cta_location.value[0]?.elements
                    .third_party_url?.value;
            case 'kontent_item_documents___products':
                return props.cta_configuration__cta_location.value[0]?.elements
                    .file?.value[0]?.url;
            case 'kontent_item_document___general':
                return props.cta_configuration__cta_location.value[0]?.elements
                    .document?.value[0]?.url;
        }
    };

    const handleClickNextA = () => {
        setCurrentAProduct((currentAPosition) => currentAPosition + 1);
        setCurrentAPosition((currentAPosition) => currentAPosition + 1);

        const leftColWidth = colA?.current?.getBoundingClientRect()?.width;

        setLeftA(leftA - leftColWidth);
    };

    const handleClickPrevA = () => {
        setCurrentAProduct((currentAPosition) => currentAPosition - 1);
        setCurrentAPosition((currentAPosition) => currentAPosition + -1);

        const leftColWidth = colA?.current?.getBoundingClientRect()?.width;

        setLeftA(leftA + leftColWidth);
    };

    const handleClickNextB = () => {
        setCurrentBProduct((currentBPosition) => currentBPosition + 1);
        setCurrentBPosition((currentBPosition) => currentBPosition + 1);

        const leftColWidth = colA?.current?.getBoundingClientRect()?.width;

        setLeftB(leftB - leftColWidth);
    };

    const handleClickPrevB = () => {
        setCurrentBProduct((currentBPosition) => currentBPosition - 1);
        setCurrentBPosition((currentBPosition) => currentBPosition + -1);
        const leftColWidth = colA?.current?.getBoundingClientRect()?.width;

        setLeftB(leftB + leftColWidth);
    };

    useEffect(() => {
        const resizeListener = () => {
            //@ts-ignore
            setWidth(columnWrap.current.getBoundingClientRect().width);
        };

        resizeListener();
        window.addEventListener('resize', resizeListener);

        return () => {
            window.removeEventListener('resize', resizeListener);
        };
    }, []);

    const renderPrevButtonA = () => (
        <ArrowLgButton
            strokeColor="#848685"
            direction="left"
            handleClick={handleClickPrevA}
            className={styles.prevArrowButtonA}
        />
    );
    const renderPrevButtonB = () => (
        <ArrowLgButton
            strokeColor="#848685"
            direction="left"
            handleClick={handleClickPrevB}
            className={styles.prevArrowButtonB}
        />
    );

    const renderNextButtonA = () => (
        <ArrowLgButton
            strokeColor="#848685"
            direction="right"
            handleClick={handleClickNextA}
            className={styles.nextArrowButtonA}
        />
    );

    const renderNextButtonB = () => (
        <ArrowLgButton
            strokeColor="#848685"
            direction="right"
            handleClick={handleClickNextB}
            className={styles.nextArrowButtonB}
        />
    );

    const renderDots = (dotsToShow: number, currentPosition: number) => {
        let dots = [];

        for (let i = 0; i < dotsToShow; i++) {
            dots.push(
                <span
                    key={i}
                    style={currentPosition == i ? { background: '#ccc' } : {}}
                ></span>
            );
        }

        return dots;
    };

    const handleCompareProductsA = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        if (event.currentTarget.value) {
            const productClicked = parseInt(event.currentTarget.value);
            setComparisonValueA(productClicked);
            //@ts-ignore
            setCompareProductA(categoryAProducts.value[productClicked]);

            if (productClicked === -1) {
                setShowComparison(false);
            }
        }
    };

    const handleCompareProductsB = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        if (event.currentTarget.value) {
            const productClicked = parseInt(event.currentTarget.value);
            setComparisonValueB(productClicked);
            //@ts-ignore
            setCompareProductB(categoryBProducts.value[productClicked]);

            if (productClicked === -1) {
                setShowComparison(false);
            }
        }
    };

    const handleComparisonClick = () => {
        if (comparisonValueA !== -1 && comparisonValueB !== -1) {
            setShowComparison(true);
        } else {
            setShowComparison(false);
        }
    };

    const renderCompareProductsAspectsA = (productAspect: any, idx: number) => {
        let aspects: any[] = [];

        if (compareProductA?.elements?.comparative_aspect?.value?.length) {
            for (
                let i = 0;
                i <
                compareProductA?.elements?.comparative_aspect?.value?.length;
                i++
            ) {
                aspects.push(
                    compareProductA?.elements?.comparative_aspect?.value[i]
                        .codename
                );
            }

            return (
                /*----- This classname inside li is coming undefined -----*/
                <li
                    key={'codenameA' + idx}
                    className={
                        aspects.includes(productAspect.codename)
                            ? `${styles.aspectProd}`
                            : `${styles.noAspectProd}`
                    }
                >
                    <span className={styles.sunlighten}></span>
                </li>
            );
        }
    };

    const renderCompareProductsMaterialsA = (
        aspectMaterial: any,
        idx: number
    ) => {
        let material: any[] = [];

        let productMaterials = compareProductA?.elements?.material?.value;

        if (productMaterials?.length) {
            for (let i = 0; i < productMaterials?.length; i++) {
                material.push(productMaterials[i]?.codename);
            }
        }

        return (
            <li
                key={'materialA' + idx}
                className={
                    material.includes(aspectMaterial.codename)
                        ? `${styles.aspectProd}`
                        : `${styles.noAspectProd}`
                }
            >
                <span className={styles.sunlighten}></span>
            </li>
        );
    };

    const renderCompareProductsMaterialsB = (
        aspectMaterial: any,
        idx: number
    ) => {
        let material: any[] = [];

        let productMaterials = compareProductB?.elements?.material?.value;

        if (productMaterials?.length) {
            for (let i = 0; i < productMaterials?.length; i++) {
                material.push(productMaterials[i]?.codename);
            }
        }

        return (
            <li
                key={'materialB' + idx}
                className={
                    material.includes(aspectMaterial.codename)
                        ? `${styles.aspectProd}`
                        : `${styles.noAspectProd}`
                }
            >
                <span className={styles.other}></span>
            </li>
        );
    };

    const renderCompareProductsAspectsB = (productAspect: any, idx: number) => {
        let aspects: any[] = [];
        if (compareProductB?.elements.comparative_aspect?.value.length) {
            for (
                let i = 0;
                i < compareProductB.elements.comparative_aspect?.value.length;
                i++
            ) {
                aspects.push(
                    compareProductB?.elements.comparative_aspect?.value[i]
                        .codename
                );
            }

            return (
                <li
                    key={'codenameB' + idx}
                    className={
                        aspects.includes(productAspect.codename)
                            ? `${styles.aspectProd}`
                            : `${styles.noAspectProd}`
                    }
                >
                    <span className={styles.other}></span>
                </li>
            );
        }
    };

    const renderComparativeAspect = (aspect: any, idx: number) => {
        return (
            <li key={'aspect' + idx} className={styles.aspectName}>
                {aspect.name}
            </li>
        );
    };

    const renderMaterials = (material: any, idx: number) => {
        let MaterialName = material.elements?.material?.value[0]?.codename;
        let MaterialImage =
            material?.elements?.image?.value[0]?.elements?.file?.value[0]?.url;

        return (
            <li
                key={'material' + idx}
                className={styles.aspectName}
                style={{
                    background: 'url(' + MaterialImage + ')',
                    color: '#ffffff',
                }}
            >
                {material.elements.material?.value[0]?.name}
            </li>
        );
    };

    const renderCategoryProducts = (product: any, idx: number) => {
        let comparativeAspectItems: any[] = [];
        for (
            let i = 0;
            i < product.elements.comparative_aspect.value.length;
            i++
        ) {
            comparativeAspectItems.push(
                product.elements.comparative_aspect.value[i].codename
            );
        }
        let comparativeMaterials: any[] = [];
        for (let i = 0; i < product.elements.material.value.length; i++) {
            comparativeMaterials.push(
                product.elements.material.value[i].codename
            );
        }

        const productCtaContentStyle = {
            '--cta-content': `'${product.elements.cta_configuration__cta_label?.value}'`,
        } as React.CSSProperties;

        return (
            <ul
                key={'product' + idx}
                className={
                    product.elements.brand.value[0]?.codename === 'sunlighten'
                        ? `${styles.sunlighten}`
                        : `${styles.other}`
                }
            >
                <li
                    className={
                        product.elements.brand.value[0]?.codename ===
                        'sunlighten'
                            ? `${styles.sunlighten}`
                            : `${styles.other}`
                    }
                >
                    {RichText(
                        product.elements.display_name,
                        undefined,
                        undefined,
                        undefined,
                        'display_name'
                    )}
                </li>
                <li className={styles.modelImage}>
                    <a
                        href={
                            product.elements.master_product.value[0]?.elements
                                ?.slug?.url
                        }
                        style={productCtaContentStyle}
                        aria-label={`View ${
                            product.elements.display_name?.value || 'product'
                        } details`}
                    >
                        <div data-kontent-element-codename="image">
                            <LazyImage
                                src={optimizeKontentImageUrl(
                                    product.elements.image.value[0]?.elements
                                        ?.file?.value[0]?.url,
                                    {
                                        ...imagePreset.card.desktop,
                                        q: 95,
                                    }
                                )}
                                srcSet={buildKontentSrcSet(
                                    product.elements.image.value[0]?.elements
                                        ?.file?.value[0]?.url,
                                    imagePreset.card.desktop.w,
                                    { fm: 'webp' as const, q: 95 }
                                )}
                                sizes="(max-width: 768px) 100vw, 33vw"
                                alt={product.elements.display_name?.value || ''}
                                {...getDisplayDimensions(
                                    (
                                        product.elements.image.value[0]
                                            ?.elements?.file?.value[0] as any
                                    )?.width,
                                    (
                                        product.elements.image.value[0]
                                            ?.elements?.file?.value[0] as any
                                    )?.height,
                                    imagePreset.card.desktop.w
                                )}
                            />
                        </div>
                    </a>
                </li>
                <div
                    className={
                        product.elements.brand.value[0]?.codename ===
                        'sunlighten'
                            ? `${styles.learnSunlighten}`
                            : `${styles.learnOther}`
                    }
                >
                    {product?.elements.cta_configuration__cta_label.value && (
                        <CtaLink
                            href={BuildLinkHref(
                                product?.elements
                                    .cta_configuration__cta_location
                            )}
                            visualStyle="outlineLight"
                        >
                            {
                                product?.elements.cta_configuration__cta_label
                                    ?.value
                            }
                        </CtaLink>
                    )}
                </div>

                {props.comparative_aspect.value.map(
                    (codename: any, idx: number) => {
                        return (
                            <li
                                key={'aspectCodename' + idx}
                                className={
                                    comparativeAspectItems.includes(
                                        codename.codename
                                    )
                                        ? `${styles.aspectProd}`
                                        : `${styles.noAspectProd}`
                                }
                            >
                                <span
                                    className={
                                        product.elements.brand.value[0]
                                            .codename === 'sunlighten'
                                            ? `${styles.sunlighten}`
                                            : `${styles.other}`
                                    }
                                ></span>
                            </li>
                        );
                    }
                )}
                {materials.map((catMaterial: any, idx: number) => {
                    return (
                        <li
                            key={'materialsCodename' + idx}
                            className={
                                comparativeMaterials.includes(
                                    catMaterial.elements.material.value[0]
                                        .codename
                                )
                                    ? `${styles.aspectProd}`
                                    : `${styles.noAspectProd}`
                            }
                        >
                            <span
                                className={
                                    product.elements.brand.value[0]
                                        ?.codename === 'sunlighten'
                                        ? `${styles.sunlighten}`
                                        : `${styles.other}`
                                }
                            ></span>
                        </li>
                    );
                })}
            </ul>
        );
    };

    return (
        <div
            className={styles.prodComparisonWrapper}
            data-kontent-item-id={props.kontentItemId}
        >
            <section className={styles.comparison}>
                <div className={styles.comparisonFilter}>
                    <h2>
                        {headline?.value ? (
                            <PlainTextElement
                                text={headline}
                                elementCodename="headline"
                            />
                        ) : (
                            'Product Comparison'
                        )}
                    </h2>
                    <span>
                        <label htmlFor="categoryA">{categoryALabel}</label>
                        <div className="select-wrapper">
                            <select
                                id="categoryA"
                                name="categoryA"
                                defaultValue=""
                                onChange={handleCompareProductsA}
                            >
                                <option value="-1">All</option>
                                {categoryAProducts.value.map(
                                    (productA, index) => (
                                        <option
                                            key={'optionA' + index}
                                            value={index}
                                            dangerouslySetInnerHTML={{
                                                __html: productA.elements
                                                    .display_name.value,
                                            }}
                                        ></option>
                                    )
                                )}
                            </select>
                        </div>
                    </span>
                    <span>
                        <label htmlFor="categoryB">{categoryBLabel}</label>
                        <div className="select-wrapper">
                            <select
                                id="categoryB"
                                name="categoryB"
                                defaultValue=""
                                onChange={handleCompareProductsB}
                            >
                                <option value="-1">All</option>
                                {categoryBProducts.value.map(
                                    (productB, index) => (
                                        <option
                                            key={'optionB' + index}
                                            value={index}
                                            dangerouslySetInnerHTML={{
                                                __html: productB.elements
                                                    .display_name.value,
                                            }}
                                        ></option>
                                    )
                                )}
                            </select>
                        </div>
                    </span>
                    <span>
                        <button onClick={handleComparisonClick}>
                            {compareCtaLabel}
                        </button>
                    </span>
                </div>
            </section>

            {!showComparison && (
                <>
                    <section className={styles.allResults}>
                        <div ref={columnWrap} className={styles.columnWrap}>
                            {!breakpoints.md && (
                                <div className={styles.columns}>
                                    <div style={{}} className={styles.col}>
                                        <div
                                            style={{
                                                width: '800px',
                                                marginLeft: leftA + 'px',
                                            }}
                                            className={styles.catAproducts}
                                        >
                                            {categoryAProducts.value.map(
                                                (product, idx) => {
                                                    return renderCategoryProducts(
                                                        product,
                                                        idx
                                                    );
                                                }
                                            )}
                                        </div>
                                        {categoryAProducts.value.length > 0 &&
                                        currentAProduct > 0
                                            ? renderPrevButtonA()
                                            : null}
                                        {currentAPosition + 1 < dotsToShowCatA
                                            ? renderNextButtonA()
                                            : null}
                                        <div className={styles.dots}>
                                            {renderDots(
                                                dotsToShowCatA,
                                                currentAPosition
                                            )}
                                        </div>
                                    </div>
                                    <div
                                        style={{}}
                                        className={styles.aspectMenu}
                                    >
                                        <ul ref={colA}>
                                            <li
                                                style={{ display: 'none' }}
                                            ></li>
                                            <li
                                                style={{ display: 'none' }}
                                            ></li>
                                            {props.comparative_aspect.value.map(
                                                (aspect, idx) => {
                                                    return renderComparativeAspect(
                                                        aspect,
                                                        idx
                                                    );
                                                }
                                            )}
                                            {props.materials.value.map(
                                                (
                                                    material: any,
                                                    idx: number
                                                ) => {
                                                    return renderMaterials(
                                                        material,
                                                        idx
                                                    );
                                                }
                                            )}
                                        </ul>
                                    </div>
                                    <div style={{}} className={styles.col}>
                                        <div
                                            style={{
                                                width: '800px',
                                                marginLeft: leftB + 'px',
                                            }}
                                            className={styles.otherProducts}
                                        >
                                            {categoryBProducts.value.map(
                                                (product, idx) => {
                                                    return renderCategoryProducts(
                                                        product,
                                                        idx
                                                    );
                                                }
                                            )}
                                        </div>
                                        {categoryBProducts.value.length > 0 &&
                                        currentBProduct > 0
                                            ? renderPrevButtonB()
                                            : null}
                                        {currentBPosition + 1 < dotsToShowCatB
                                            ? renderNextButtonB()
                                            : null}
                                        <div className={styles.dots}>
                                            {renderDots(
                                                dotsToShowCatB,
                                                currentBPosition
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {breakpoints.md && (
                                <div
                                    className={styles.columns}
                                    style={newWidth}
                                >
                                    <ul>
                                        <li></li>
                                        <li>Model</li>
                                        {props.comparative_aspect.value.map(
                                            (aspect, idx) => {
                                                return renderComparativeAspect(
                                                    aspect,
                                                    idx
                                                );
                                            }
                                        )}
                                        {props.materials.value.map(
                                            (material: any, idx: number) => {
                                                return renderMaterials(
                                                    material,
                                                    idx
                                                );
                                            }
                                        )}
                                    </ul>

                                    {categoryAProducts.value?.map(
                                        (product, idx) => {
                                            return renderCategoryProducts(
                                                product,
                                                idx
                                            );
                                        }
                                    )}
                                    {categoryBProducts.value?.map(
                                        (product, idx) => {
                                            return renderCategoryProducts(
                                                product,
                                                idx
                                            );
                                        }
                                    )}
                                </div>
                            )}
                        </div>
                    </section>
                </>
            )}

            <section className={styles.compareResults}>
                {showComparison && (
                    <>
                        <div className={styles.columnWrap} ref={columnWrap}>
                            <div className={styles.compareCol}>
                                {compareProductA && (
                                    <div className={styles.compareColA}>
                                        <h2>
                                            {RichText(
                                                compareProductA.elements
                                                    .display_name,
                                                undefined,
                                                undefined,
                                                undefined,
                                                'display_name'
                                            )}
                                        </h2>
                                        <div className={styles.productImage}>
                                            <div data-kontent-element-codename="image">
                                                <LazyImage
                                                    src={optimizeKontentImageUrl(
                                                        compareProductA
                                                            ?.elements.image
                                                            ?.value[0].elements
                                                            .file?.value[0]
                                                            ?.url,
                                                        {
                                                            ...imagePreset.card
                                                                .desktop,
                                                            q: 95,
                                                        }
                                                    )}
                                                    srcSet={buildKontentSrcSet(
                                                        compareProductA
                                                            ?.elements.image
                                                            ?.value[0].elements
                                                            .file?.value[0]
                                                            ?.url,
                                                        imagePreset.card.desktop
                                                            .w,
                                                        {
                                                            fm: 'webp' as const,
                                                            q: 95,
                                                        }
                                                    )}
                                                    sizes="(max-width: 768px) 100vw, 33vw"
                                                    alt={
                                                        compareProductA
                                                            ?.elements
                                                            ?.display_name
                                                            ?.value || ''
                                                    }
                                                    {...getDisplayDimensions(
                                                        (
                                                            compareProductA
                                                                ?.elements.image
                                                                ?.value[0]
                                                                ?.elements.file
                                                                ?.value[0] as any
                                                        )?.width,
                                                        (
                                                            compareProductA
                                                                ?.elements.image
                                                                ?.value[0]
                                                                ?.elements.file
                                                                ?.value[0] as any
                                                        )?.height,
                                                        imagePreset.card.desktop
                                                            .w
                                                    )}
                                                />
                                            </div>
                                            <CtaLink
                                                href={BuildLinkHref(
                                                    compareProductA?.elements
                                                        .cta_configuration__cta_location
                                                )}
                                                visualStyle="outlineDark"
                                            >
                                                {
                                                    compareProductA?.elements
                                                        .cta_configuration__cta_label
                                                        ?.value
                                                }
                                            </CtaLink>
                                        </div>
                                        <ul
                                            className={styles.comparativeAspect}
                                        >
                                            {props.comparative_aspect.value.map(
                                                (aspect, idx) => {
                                                    return renderComparativeAspect(
                                                        aspect,
                                                        idx
                                                    );
                                                }
                                            )}
                                            {props.materials.value.map(
                                                (
                                                    material: any,
                                                    idx: number
                                                ) => {
                                                    return renderMaterials(
                                                        material,
                                                        idx
                                                    );
                                                }
                                            )}
                                        </ul>
                                        <ul
                                            className={
                                                styles.comparativeResults
                                            }
                                        >
                                            {props.comparative_aspect.value.map(
                                                (
                                                    productAspect: any,
                                                    idx: number
                                                ) => {
                                                    return renderCompareProductsAspectsA(
                                                        productAspect,
                                                        idx
                                                    );
                                                }
                                            )}
                                            {props.materials.value.map(
                                                (material, idx) => {
                                                    return renderCompareProductsMaterialsA(
                                                        material?.elements
                                                            ?.material.value[0],
                                                        idx
                                                    );
                                                }
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </div>

                            <div className={styles.compareCol}>
                                {compareProductB && (
                                    <div>
                                        <h2>
                                            {RichText(
                                                compareProductB.elements
                                                    .display_name,
                                                undefined,
                                                undefined,
                                                undefined,
                                                'display_name'
                                            )}
                                        </h2>
                                        <div
                                            style={{ float: 'left' }}
                                            className={styles.productImage}
                                            data-kontent-element-codename="image"
                                        >
                                            <LazyImage
                                                src={optimizeKontentImageUrl(
                                                    compareProductB?.elements
                                                        .image?.value[0]
                                                        ?.elements?.file
                                                        ?.value[0]?.url,
                                                    {
                                                        ...imagePreset.card
                                                            .desktop,
                                                        q: 95,
                                                    }
                                                )}
                                                srcSet={buildKontentSrcSet(
                                                    compareProductB?.elements
                                                        .image?.value[0]
                                                        ?.elements?.file
                                                        ?.value[0]?.url,
                                                    imagePreset.card.desktop.w,
                                                    {
                                                        fm: 'webp' as const,
                                                        q: 95,
                                                    }
                                                )}
                                                sizes="(max-width: 768px) 100vw, 33vw"
                                                alt={
                                                    compareProductB?.elements
                                                        ?.display_name?.value ||
                                                    ''
                                                }
                                                {...getDisplayDimensions(
                                                    (
                                                        compareProductB
                                                            ?.elements.image
                                                            ?.value[0]?.elements
                                                            ?.file
                                                            ?.value[0] as any
                                                    )?.width,
                                                    (
                                                        compareProductB
                                                            ?.elements.image
                                                            ?.value[0]?.elements
                                                            ?.file
                                                            ?.value[0] as any
                                                    )?.height,
                                                    imagePreset.card.desktop.w
                                                )}
                                            />
                                        </div>

                                        <ul
                                            style={{ float: 'left' }}
                                            className={styles.comparativeAspect}
                                        >
                                            {props.comparative_aspect.value.map(
                                                (aspect, idx) => {
                                                    return renderComparativeAspect(
                                                        aspect,
                                                        idx
                                                    );
                                                }
                                            )}

                                            {props.materials.value.map(
                                                (
                                                    material: any,
                                                    idx: number
                                                ) => {
                                                    return renderMaterials(
                                                        material,
                                                        idx
                                                    );
                                                }
                                            )}
                                        </ul>
                                        <ul
                                            style={{ float: 'left' }}
                                            className={`${styles.comparativeResults} ${styles.comparativeResultsB}`}
                                        >
                                            {props.comparative_aspect.value.map(
                                                (
                                                    productAspect: any,
                                                    idx: number
                                                ) => {
                                                    return renderCompareProductsAspectsB(
                                                        productAspect,
                                                        idx
                                                    );
                                                }
                                            )}

                                            {props.materials.value.map(
                                                (material, idx) => {
                                                    return renderCompareProductsMaterialsB(
                                                        material?.elements
                                                            ?.material.value[0],
                                                        idx
                                                    );
                                                }
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </section>
            {props.cta_configuration__cta_label?.value ? (
                <div className={styles.ctaContainer}>
                    <CtaLink
                        href={downloadCTA()}
                        target={
                            ctaWindowAction === 'new_window'
                                ? '_blank'
                                : undefined
                        }
                    >
                        {props.cta_configuration__cta_label?.value}
                    </CtaLink>
                </div>
            ) : (
                <></>
            )}
        </div>
    );
};

export default ProductComparison;

function setNewWidth(
    columnProducts: columnProducts,
    width: width,
    colWidth: colWidth
) {
    let newWidth = colWidth * columnProducts + 200;
    const galleryInsideWidth: React.CSSProperties = {
        width: newWidth,
    };

    return galleryInsideWidth;
}

export const fragmentProductComparison = graphql`
    fragment ProductComparison on kontent_item_component___product_comparison {
        elements {
            headline {
                value
            }
            comparative_aspect {
                value {
                    codename
                    name
                }
            }
            category_a_label {
                value
            }
            category_a_master_color {
                ...kontentColor
            }
            category_a_products {
                value {
                    ...kontentComparisonProduct
                }
            }
            category_b_label {
                value
            }
            category_b_master_color {
                ...kontentColor
            }
            category_b_products {
                value {
                    ...kontentComparisonProduct
                }
            }
            compare_cta_label {
                value
            }
            materials {
                value {
                    ... on kontent_item_data___material {
                        elements {
                            material {
                                value {
                                    name
                                    codename
                                }
                            }
                            image {
                                value {
                                    ... on kontent_item_media___image {
                                        elements {
                                            file {
                                                value {
                                                    url
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            name_for_display {
                                value
                            }
                        }
                    }
                }
            }
            cta_configuration__cta_label {
                value
            }
            cta_configuration__cta_location {
                value {
                    __typename
                    ... on kontent_item_document___general {
                        elements {
                            document {
                                value {
                                    url
                                }
                            }
                        }
                    }
                    ... on kontent_item_documents___products {
                        elements {
                            file {
                                value {
                                    url
                                }
                            }
                        }
                    }
                    ... on kontent_item_layout___master_page {
                        elements {
                            slug {
                                url
                            }
                        }
                    }
                    ... on kontent_item_content_item___third_party_link {
                        elements {
                            third_party_url {
                                value
                            }
                        }
                    }
                }
            }
            cta_configuration__cta_window_action {
                value {
                    codename
                }
            }
        }
    }
`;
